import React, {FC} from 'react';
import {connect} from 'react-redux';

import {makeContactsManagerCardGroupsUrl} from '../../../../../../../src/helpers/contactsManagerHelpers';
import {parseTeamNamespace} from '../../../../../../../src/helpers/models/namespaceHelpers';
import {ResolveDispatchProps} from '../../../../../../../src/helpers/reduxHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../src/services/tracking';
import {
  addCardGroupToCardsAsyncAction,
  deleteCardGroupsAsyncAction,
  fetchCardGroupAction,
} from '../../../../../../../src/store/data/cardGroups/cardGroupsActions';
import {getCardGroups} from '../../../../../../../src/store/data/dataSelectors';
import {getTeams} from '../../../../../../../src/store/data/teams/teamsSelectors';
import {
  makeGetRouteCardGroupId,
  makeGetSelectedCardIds,
} from '../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {contactsManagerTableAddCardIdRefsAction} from '../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {navigateToLinkAction} from '../../../../../../../src/store/stateless/navigationActions';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {useInitialEffect} from '../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {CardGroupContactsPage} from './cardGroupContactsPage';

/*
 * Props.
 */

type CardGroupContactsPageContainerComponentStateProps = ResolveStateProps<typeof mapStateToProps>;
type CardGroupContactsPageContainerComponentDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type CardGroupContactsPageContainerComponentProps = CardGroupContactsPageContainerComponentStateProps &
  CardGroupContactsPageContainerComponentDispatchProps;

/*
 * Selectors.
 */

function makeGetCardGroup(getCardGroupId: ReturnType<typeof makeGetRouteCardGroupId>) {
  return createSelector([getCardGroupId, getCardGroups], (cardGroupId, cardGroups) => {
    if (!cardGroupId) {
      return undefined;
    }
    return cardGroups[cardGroupId];
  });
}

function makeGetTeam(getCardGroup: ReturnType<typeof makeGetCardGroup>) {
  return createSelector([getCardGroup, getTeams], (cardGroup, teams) => {
    const teamId = cardGroup && parseTeamNamespace(cardGroup.namespace);
    if (!teamId) {
      return undefined;
    }
    return teams[teamId];
  });
}

/*
 * Component.
 */

const CardGroupContactsPageContainerComponent: FC<CardGroupContactsPageContainerComponentProps> = (props) => {
  const {
    cardGroup,
    cardGroupId,
    selectedCardIds,
    team,
    addCardGroupToCardsAsync,
    navigateToLink,
    fetchCardGroup,
    onDeleteCardGroupsAsync,
    contactsManagerTableAddCardIdRefs,
  } = props;

  const navigateBackToCardGroups = () => navigateToLink(makeContactsManagerCardGroupsUrl());

  useInitialEffect(() => {
    if (!cardGroupId) {
      navigateBackToCardGroups();
      return;
    }

    const fetchCardGroupData = async () => {
      try {
        await fetchCardGroup(cardGroupId);
      } catch (err) {
        navigateBackToCardGroups();
      }
    };

    // if no cardGroup, try to fetch cardGroup with id
    if (!cardGroup) {
      fetchCardGroupData();
    } else {
      trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CARD_GROUPS_SECTION_VIEW, {
        list_type: ContactsManagerParametersTypesEnum.LIST_CONTACTS.toLowerCase(),
      });
    }
  });

  return (
    <CardGroupContactsPage
      cardGroup={cardGroup}
      selectedCardIds={selectedCardIds}
      team={team}
      addCardGroupToCardsAsync={addCardGroupToCardsAsync}
      navigateBackToLists={navigateBackToCardGroups}
      onDeleteCardGroupsAsync={onDeleteCardGroupsAsync}
      contactsManagerTableAddCardIdRefs={contactsManagerTableAddCardIdRefs}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => {
  const getCardGroupId = makeGetRouteCardGroupId();
  const getCardGroup = makeGetCardGroup(getCardGroupId);
  return {
    cardGroupId: getCardGroupId,
    cardGroup: getCardGroup,
    selectedCardIds: makeGetSelectedCardIds(),
    team: makeGetTeam(getCardGroup),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToLinkAction,
  fetchCardGroup: fetchCardGroupAction,
  addCardGroupToCardsAsync: addCardGroupToCardsAsyncAction,
  contactsManagerTableAddCardIdRefs: (cardIds: ReadonlyArray<number>) =>
    contactsManagerTableAddCardIdRefsAction(cardIds, true),
  onDeleteCardGroupsAsync: deleteCardGroupsAsyncAction,
}));

export const CardGroupContactsPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CardGroupContactsPageContainerComponent);
